import { useRef, useState, useMemo, useCallback } from 'react';
import useFetchJobChangeProcessesInfinite from '../../job_change_processes/api/useFetchJobChangeProcessesInfinite';
import JobChangeProcessListItem from '../../job_change_processes/components/list/JobChangeProcessListItem';
import JobChangeProcessStatusUpdateModal from '../../job_change_processes/components/modal/JobChangeProcessStatusUpdateModal';
import ListItemLoading from '../../job_change_processes/components/list/ListItemLoading';
import ListItemNoData from '../../job_change_processes/components/list/ListItemNoData';
import useOnClickOutside from '../../job_change_processes/hooks/useOnClickOutside';
import useRollbackHandler from '../../job_change_processes/hooks/useRollbackHandler';
import JobChangeProcessSidebar from '../../job_change_processes/components/sidebar/JobChangeProcessDetailSidebar';
import JobChangeProcessLogStatusUpdateModal from '../../job_change_processes/components/modal/JobChangeProcessLogStatusUpdateModal';
import clsx from 'clsx';
import useActiveUsers from '../../active_users/hooks/useActiveUsers';
import useFetchIam from '../../api/useFetchIam';
import useUpdateCandidateProfile from '../../candidates/api/useUpdateCandidateProfile';
import { showErrorToast } from '../../job_change_processes/lib/toast';

type Props = {
  companyId: string;
};

export default function CompaniesMatchingCard({ companyId }: Props) {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const { data: activeUsersData } = useActiveUsers();
  const activeUsers = activeUsersData?.activeUsers ?? [];
  const { data: iam } = useFetchIam();
  const currentUserId = iam?.current_user?.id;
  const { mutateAsync: updateCandidate } = useUpdateCandidateProfile();

  const searchParams = useMemo(
    () => ({
      company_id: Number(companyId),
      result: ['processing', 'rejected', 'declined', 'offered', 'closed'],
    }),
    [companyId]
  );

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading, refetch } =
    useFetchJobChangeProcessesInfinite(searchParams);

  const handleUpdateCandidateAssignees = useCallback(
    async (candidateProfileId: number, userIds: number[]) => {
      try {
        await updateCandidate({ candidateProfileId, organization_user_ids: userIds });
        await refetch();
      } catch (e) {
        showErrorToast('担当者の更新に失敗しました');
      }
    },
    [updateCandidate, refetch]
  );

  const jobChangeProcesses = useMemo(() => {
    if (!data?.pages) return [];
    return data.pages.flatMap((page) => page.records);
  }, [data?.pages]);
  const [statusModalOpen, setStatusModalOpen] = useState(false);
  const [logStatusModalOpen, setLogStatusModalOpen] = useState(false);
  const listRef = useRef<HTMLDivElement>(null);
  const sidebarRef = useRef<HTMLDivElement>(null);
  useOnClickOutside(sidebarRef, () => {
    setIsSidebarOpen(false);
    setIsVisible(false);
  });

  const openSidebar = (isValue: boolean) => {
    setIsSidebarOpen(isValue);
    setIsVisible(isValue);
  };

  const closeSidebar = () => {
    setIsSidebarOpen(false);
    setTimeout(() => setIsVisible(false), 50);
  };

  // スクロールイベントハンドラーを追加
  const handleScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      const target = e.currentTarget;
      const { scrollTop, scrollHeight, clientHeight } = target;
      const distanceToBottom = scrollHeight - (scrollTop + clientHeight);

      // 底から200px以内でかつ次のページがある場合にフェッチ
      if (distanceToBottom < 200 && hasNextPage && !isFetchingNextPage) {
        fetchNextPage();
      }
    },
    [hasNextPage, isFetchingNextPage, fetchNextPage]
  );

  const scrollToTarget = () => {
    listRef?.current?.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  };

  const updateJobchangeProcesses = async () => {
    await refetch();
    scrollToTarget();
  };

  const { handleRollback, isRollbackPending } = useRollbackHandler(async () => {
    closeSidebar();
    await updateJobchangeProcesses();
  });

  return (
    <>
      <div className="relative h-[calc(100vh_-_184px)]">
        <div className="p-lg pb-0" ref={listRef}>
          <div className="flex h-full flex-col">
            <div className="flex h-[36px] shrink-0 items-center justify-around rounded-s bg-tag-gray px-6">
              <p className="text-sm font-bold">求職者</p>
              <p className="text-sm font-bold">企業名 / 求人</p>
              <p className="text-sm font-bold">ステータス</p>
              <p className="text-sm font-bold">選考日程</p>
              <p className="text-sm font-bold">メモ</p>
            </div>
            <div
              className="flex max-h-[calc(100vh_-_248px)] min-h-0 flex-1 flex-col gap-3 overflow-y-auto px-1 pt-4"
              onScroll={handleScroll}
            >
              {isLoading ? (
                <>
                  {Array.from({ length: 10 }).map((_, key) => {
                    return <ListItemLoading key={key} />;
                  })}
                </>
              ) : (
                <>
                  {jobChangeProcesses.length === 0 ? (
                    <div className="mt-[100px]">
                      <ListItemNoData />
                    </div>
                  ) : (
                    <>
                      {jobChangeProcesses.map((j, key) => {
                        return (
                          <JobChangeProcessListItem
                            key={`${j.id}-${key}`}
                            jobChangeProcess={j}
                            openSidebar={openSidebar}
                            statusModalOpen={() => setStatusModalOpen(true)}
                            jobchangeProcessesRefetch={updateJobchangeProcesses}
                            isSidebarOpen={isSidebarOpen}
                            activeUsers={activeUsers}
                            currentUserId={currentUserId}
                            onUpdateCandidateAssignees={handleUpdateCandidateAssignees}
                          />
                        );
                      })}
                      {/* 追加のローディング表示 */}
                      {isFetchingNextPage && (
                        <>
                          {Array.from({ length: 5 }).map((_, key) => (
                            <ListItemLoading key={`loading-${key}`} />
                          ))}
                        </>
                      )}
                    </>
                  )}
                </>
              )}
            </div>
            <JobChangeProcessStatusUpdateModal
              open={statusModalOpen}
              onOpenChange={setStatusModalOpen}
              jobchangeProcessesRefetch={updateJobchangeProcesses}
            />
          </div>
        </div>
        <div>
          {isVisible && (
            <div
              ref={sidebarRef}
              className={clsx(
                'absolute top-0 right-0 z-50 h-full w-[415px] overflow-hidden shadow-[0px_0px_6px_0px_#00000033]',
                isSidebarOpen ? 'animate-slideInRight' : 'animate-fadeOutRight'
              )}
              style={{
                maxHeight: 'calc(100vh - 202px)',
              }}
            >
              <div className="flex h-full flex-col overflow-hidden">
                <JobChangeProcessSidebar
                  closeSidebar={closeSidebar}
                  statusOpen={() => setLogStatusModalOpen(true)}
                  isSidebarOpen={isSidebarOpen}
                  onRollback={handleRollback}
                  isRollbackPending={isRollbackPending}
                />
              </div>
            </div>
          )}
          <JobChangeProcessLogStatusUpdateModal
            open={logStatusModalOpen}
            onOpenChange={setLogStatusModalOpen}
            jobchangeProcessesRefetch={updateJobchangeProcesses}
          />
        </div>
      </div>
    </>
  );
}
